import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CustomAxios from '../utils/CustomAxios';
import { useSelector } from 'react-redux';

const PaymentSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.user.id);
  const hubId = localStorage.getItem('selected_hub');
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  const [cancelError, setCancelError] = useState(null);
  const [timer, setTimer] = useState(30); // 30-second cancellation window
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelStatus, setCancelStatus] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      if (!orderId || !userId) {
        setError('Missing order ID or user ID');
        setLoading(false);
        return;
      }

      try {
        const response = await CustomAxios.get(
          `check_payment_status/${orderId}/${userId}/`
        );

        // Debug logging
        console.log('=== Payment Status Response Debug ===');
        console.log('Full response:', response);
        console.log('Response data:', response.data);
        console.log('Response data type:', typeof response.data);
        console.log('Payment status value:', response.data?.payment_status);
        console.log('Payment status type:', typeof response.data?.payment_status);
        console.log('Payment status === true?', response.data?.payment_status === true);
        console.log('Payment status == true?', response.data?.payment_status == true);
        console.log('All response.data keys:', Object.keys(response.data || {}));
        console.log('=====================================');

        if (response.data?.payment_status === true) {
          setOrderDetails(response.data);
          console.log('Order details loaded:', response.data);
          console.log('Order status:', response.data?.status);
          // Check if order is already cancelled
          if (response.data?.status === 'cancelled') {
            console.log('Order is already cancelled');
            setCancelStatus(true);
            setTimer(0);
            setTimerStarted(false);
          } else {
            console.log('Starting timer for order cancellation');
            setTimer(30);
            setTimerStarted(true);
          }
        } else {
          console.error('Payment status check failed. Response:', response.data);
          setError('Payment status could not be verified');
        }
      } catch (err) {
        console.error('Payment status error:', err);
        setError('Failed to verify payment status');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentStatus();
  }, [orderId, userId]);

  // Timer countdown effect - only start after order details are loaded
  useEffect(() => {
    console.log('Timer effect:', { timerStarted, timer, cancelStatus, loading, orderDetails: !!orderDetails });
    if (timerStarted && timer > 0 && !cancelStatus && orderDetails) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          const newTimer = prev <= 1 ? 0 : prev - 1;
          return newTimer;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else if (timerStarted && timer === 0 && !cancelStatus && orderDetails) {
      navigate(`/order-tracking/${orderId}`);
    }
  }, [timer, cancelStatus, navigate, orderId, timerStarted, orderDetails]);

  // Format timer as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle cancel order
  const handleCancelOrder = async () => {
    setCancelLoading(true);
    try {
      
      if (!hubId) {
        setCancelError('Hub ID not found. Cannot cancel order.');
        setConfirmCancel(false);
        setCancelLoading(false);
        return;
      }

      setCancelError(null);
      const response = await CustomAxios.post('/cancel_order/', {
        user_id: userId,
        order_id: orderId,
        hub_id: hubId,
        cancelled_by: 'user'
      });

      if (response.data?.message === 'refund initiated successfully' || response.data?.message === 'Order already refunded') {
        setConfirmCancel(false);
        setCancelStatus(true);
        setTimer(0);
        setCancelError(null);
      } else {
        setCancelError('Failed to cancel order. Please try again.');
        setConfirmCancel(false);
      }
    } catch (error) {
      console.error('Cancel order error:', error);
      setCancelError(error?.response?.data?.error || error?.response?.data?.message || 'Failed to cancel order. Please contact support.');
      setConfirmCancel(false);
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/home/dashboard')}>
          Back to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, textAlign: 'center', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center',alignItems: 'center' }}>
      {cancelStatus ? (
        <CancelIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
      ) : (
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      )}
      
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
        {cancelStatus ? 'Order Cancelled' : 'Order Placed Successfully!'}
      </Typography>
      
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        Order ID: {orderId}
      </Typography>
      
      {orderDetails?.total_amount && (
        <Typography variant="body1" sx={{ mb: 3 }}>
          Total Amount: ₹{parseFloat(orderDetails.total_amount).toFixed(2)}
        </Typography>
      )}

      {cancelStatus && (
        <Alert severity="success" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          Your order has been cancelled, and the refund has been successfully initiated.
          The refund amount will be credited to your account within 5 to 7 business days.
        </Alert>
      )}

      {cancelError && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          {cancelError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, flexWrap: 'wrap' }}>
        {!cancelStatus && (
          <Button
            variant="contained"
            onClick={() => navigate(`/order-tracking/${orderId}`)}
            sx={{ textTransform: 'none' }}
          >
            Track Order
          </Button>
        )}
        
        {(() => {
          const shouldShow = !cancelStatus && orderDetails && (timer > 0 || timerStarted);
          // console.log('Button render check:', { cancelStatus, timer, orderDetails: !!orderDetails, timerStarted, shouldShow });
          return shouldShow;
        })() && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<AccessTimeIcon />}
            onClick={() => setConfirmCancel(true)}
            sx={{ textTransform: 'none' }}
          >
            Cancel Order ({timer > 0 ? formatTime(timer) : '00:00'})
          </Button>
        )}
        
        <Button 
          variant={cancelStatus ? 'contained' : 'outlined'}
          onClick={() => navigate('/home/dashboard')}
          sx={{ textTransform: 'none', width: '100%' }}
        >
          {cancelStatus ? 'Back to Home' : 'Back to Home'}
        </Button>
      </Box>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={confirmCancel} onClose={() => !cancelLoading && setConfirmCancel(false)}>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel your Order: {orderId}?
          </Typography>
          <Typography sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            The refund will be processed and credited to your account within 5 to 7 business days.
          </Typography>
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setConfirmCancel(false);
              setCancelError(null);
            }} 
            disabled={cancelLoading}
            sx={{ textTransform: 'none' }}
          >
            No, Keep Order
          </Button>
          <Button 
            onClick={handleCancelOrder} 
            color="error"
            variant="contained"
            disabled={cancelLoading}
            sx={{ textTransform: 'none' }}
          >
            {cancelLoading ? 'Cancelling...' : 'Yes, Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentSuccess;
